import React, { useState } from 'react';
import { TrendingUp, Award, DollarSign, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';

const RecommendationCard = ({ recommendation, rank }) => {
  const [expanded, setExpanded] = useState(false);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-blue-600 bg-blue-100';
    if (score >= 40) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-blue-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-indigo-300 hover:shadow-lg transition-all duration-200 bg-gradient-to-br from-white to-gray-50">
      {/* Header */}
      <div className="flex items-start gap-4 mb-4">
        {/* Rank Badge */}
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg shadow-md">
            #{rank}
          </div>
        </div>

        {/* Player Info */}
        <div className="flex-1">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">{recommendation?.playerName}</h3>
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full font-medium">
                  {recommendation?.position}
                </span>
                <span>{recommendation?.age} years</span>
                <span>•</span>
                <span>{recommendation?.currentClub}</span>
              </div>
            </div>

            {/* Compatibility Score */}
            <div className="text-center flex-shrink-0">
              <div className={`text-3xl font-bold ${getScoreColor(recommendation?.compatibilityScore)}`}>
                {recommendation?.compatibilityScore}%
              </div>
              <p className="text-xs text-gray-500 mt-1">Match Score</p>
            </div>
          </div>
        </div>
      </div>

      {/* Match Reasoning */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
        <p className="text-gray-800 leading-relaxed">{recommendation?.matchReasoning}</p>
      </div>

      {/* Key Strengths */}
      <div className="mb-4">
        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Award className="w-4 h-4 text-yellow-600" />
          Key Strengths
        </h4>
        <div className="flex flex-wrap gap-2">
          {recommendation?.keyStrengths?.map((strength, index) => (
            <span
              key={index}
              className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium"
            >
              {strength}
            </span>
          ))}
        </div>
      </div>

      {/* Fit Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {recommendation?.technicalFit !== undefined && (
          <div>
            <p className="text-xs text-gray-600 mb-1">Technical Fit</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getScoreBgColor(recommendation?.technicalFit)}`}
                  style={{ width: `${recommendation?.technicalFit}%` }}
                ></div>
              </div>
              <span className="text-sm font-semibold text-gray-900">{recommendation?.technicalFit}%</span>
            </div>
          </div>
        )}

        {recommendation?.physicalFit !== undefined && (
          <div>
            <p className="text-xs text-gray-600 mb-1">Physical Fit</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getScoreBgColor(recommendation?.physicalFit)}`}
                  style={{ width: `${recommendation?.physicalFit}%` }}
                ></div>
              </div>
              <span className="text-sm font-semibold text-gray-900">{recommendation?.physicalFit}%</span>
            </div>
          </div>
        )}

        {recommendation?.experienceFit !== undefined && (
          <div>
            <p className="text-xs text-gray-600 mb-1">Experience Fit</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getScoreBgColor(recommendation?.experienceFit)}`}
                  style={{ width: `${recommendation?.experienceFit}%` }}
                ></div>
              </div>
              <span className="text-sm font-semibold text-gray-900">{recommendation?.experienceFit}%</span>
            </div>
          </div>
        )}

        {recommendation?.valueFit !== undefined && (
          <div>
            <p className="text-xs text-gray-600 mb-1">Value Fit</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getScoreBgColor(recommendation?.valueFit)}`}
                  style={{ width: `${recommendation?.valueFit}%` }}
                ></div>
              </div>
              <span className="text-sm font-semibold text-gray-900">{recommendation?.valueFit}%</span>
            </div>
          </div>
        )}
      </div>

      {/* Market Value */}
      {recommendation?.marketValue && (
        <div className="flex items-center gap-2 text-gray-700 mb-4">
          <DollarSign className="w-4 h-4 text-green-600" />
          <span className="font-semibold">Market Value:</span>
          <span>{recommendation?.marketValue?.toLocaleString()} {recommendation?.currency || 'EUR'}</span>
        </div>
      )}

      {/* Expandable Section */}
      {(recommendation?.potentialConcerns?.length > 0 || recommendation?.transferFeasibility) && (
        <div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between text-indigo-600 hover:text-indigo-800 font-medium py-2 border-t border-gray-200 transition-colors"
          >
            <span>View Additional Details</span>
            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expanded && (
            <div className="mt-4 space-y-4">
              {/* Potential Concerns */}
              {recommendation?.potentialConcerns?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-600" />
                    Potential Concerns
                  </h4>
                  <ul className="space-y-1">
                    {recommendation?.potentialConcerns?.map((concern, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700 text-sm">
                        <span className="w-1.5 h-1.5 bg-orange-600 rounded-full mt-2 flex-shrink-0"></span>
                        <span>{concern}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Transfer Feasibility */}
              {recommendation?.transferFeasibility && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    Transfer Feasibility
                  </h4>
                  <p className="text-gray-700 text-sm bg-blue-50 px-4 py-3 rounded-lg">
                    {recommendation?.transferFeasibility}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecommendationCard;